import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { Text, Card, Chip, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const SuperAdminDashboardScreen = ({ navigation }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboard = async () => {
    try {
      const data = await api.superAdmin.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
  }, []);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={{ marginTop: 12, color: '#666' }}>Cargando dashboard...</Text>
      </View>
    );
  }

  const stats = dashboard || {};

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
    >
      <Text style={styles.title}>Panel Super Admin</Text>

      {/* KPI Cards */}
      <View style={styles.kpiRow}>
        <TouchableOpacity style={[styles.kpiCard, { backgroundColor: '#E3F2FD' }]} onPress={() => navigation.navigate('TenantsList')}>
          <Ionicons name="business" size={28} color="#1565C0" />
          <Text style={styles.kpiValue}>{stats.total_tenants || 0}</Text>
          <Text style={styles.kpiLabel}>Total Empresas</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.kpiCard, { backgroundColor: '#E8F5E9' }]} onPress={() => navigation.navigate('TenantsList')}>
          <Ionicons name="checkmark-circle" size={28} color="#2E7D32" />
          <Text style={styles.kpiValue}>{stats.tenants_activos || 0}</Text>
          <Text style={styles.kpiLabel}>Activos</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.kpiRow}>
        <TouchableOpacity style={[styles.kpiCard, { backgroundColor: '#FFF3E0' }]} onPress={() => navigation.navigate('Solicitudes')}>
          <Ionicons name="time" size={28} color="#E65100" />
          <Text style={styles.kpiValue}>{stats.solicitudes_pendientes || 0}</Text>
          <Text style={styles.kpiLabel}>Solicitudes Pendientes</Text>
        </TouchableOpacity>

        <View style={[styles.kpiCard, { backgroundColor: '#F3E5F5' }]}>
          <Ionicons name="people" size={28} color="#6A1B9A" />
          <Text style={styles.kpiValue}>{stats.total_usuarios || 0}</Text>
          <Text style={styles.kpiLabel}>Total Usuarios</Text>
        </View>
      </View>

      {/* Acciones rapidas */}
      <Text style={styles.sectionTitle}>Acciones Rapidas</Text>
      <View style={styles.actionsRow}>
        <TouchableOpacity style={styles.actionButton} onPress={() => navigation.navigate('TenantsList')}>
          <Ionicons name="business-outline" size={24} color="#007bff" />
          <Text style={styles.actionLabel}>Empresas</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.actionButton} onPress={() => navigation.navigate('Solicitudes')}>
          <Ionicons name="document-text-outline" size={24} color="#FF9800" />
          <Text style={styles.actionLabel}>Solicitudes</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.actionButton} onPress={() => navigation.navigate('PlanesList')}>
          <Ionicons name="pricetag-outline" size={24} color="#4CAF50" />
          <Text style={styles.actionLabel}>Planes</Text>
        </TouchableOpacity>
      </View>

      {/* Tenants recientes */}
      {stats.tenants_recientes && stats.tenants_recientes.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Empresas Recientes</Text>
          {stats.tenants_recientes.map((tenant) => (
            <Card key={tenant.id} style={styles.tenantCard}>
              <Card.Content style={styles.tenantRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.tenantName}>{tenant.nombre}</Text>
                  <Text style={styles.tenantSlug}>{tenant.slug}</Text>
                </View>
                <Chip
                  style={{ backgroundColor: tenant.estado === 'activo' ? '#E8F5E9' : '#FFF3E0' }}
                  textStyle={{ fontSize: 11, color: tenant.estado === 'activo' ? '#2E7D32' : '#E65100' }}
                >
                  {tenant.estado}
                </Chip>
              </Card.Content>
            </Card>
          ))}
        </>
      )}

      {/* Solicitudes pendientes */}
      {stats.solicitudes_recientes && stats.solicitudes_recientes.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Solicitudes Pendientes</Text>
          {stats.solicitudes_recientes.map((sol) => (
            <TouchableOpacity
              key={sol.id}
              onPress={() => navigation.navigate('SolicitudDetalle', { solicitud: sol })}
            >
              <Card style={styles.solicitudCard}>
                <Card.Content style={styles.tenantRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.tenantName}>{sol.nombre_empresa}</Text>
                    <Text style={styles.tenantSlug}>{sol.email_contacto}</Text>
                  </View>
                  <Chip style={{ backgroundColor: '#FFF3E0' }} textStyle={{ fontSize: 11, color: '#E65100' }}>
                    Pendiente
                  </Chip>
                </Card.Content>
              </Card>
            </TouchableOpacity>
          ))}
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { padding: 16, paddingBottom: 40 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  title: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 16 },
  kpiRow: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  kpiCard: { flex: 1, borderRadius: 12, padding: 16, alignItems: 'center' },
  kpiValue: { fontSize: 28, fontWeight: 'bold', color: '#333', marginTop: 8 },
  kpiLabel: { fontSize: 12, color: '#666', marginTop: 4, textAlign: 'center' },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#333', marginTop: 20, marginBottom: 12 },
  actionsRow: { flexDirection: 'row', gap: 12 },
  actionButton: { flex: 1, backgroundColor: 'white', borderRadius: 12, padding: 16, alignItems: 'center', elevation: 1 },
  actionLabel: { fontSize: 13, color: '#333', marginTop: 8, fontWeight: '500' },
  tenantCard: { marginBottom: 8, backgroundColor: 'white' },
  tenantRow: { flexDirection: 'row', alignItems: 'center' },
  tenantName: { fontSize: 15, fontWeight: '600', color: '#333' },
  tenantSlug: { fontSize: 12, color: '#888', marginTop: 2 },
  solicitudCard: { marginBottom: 8, backgroundColor: 'white' },
});

export default SuperAdminDashboardScreen;
